import { Box, Grid, Typography, CircularProgress, Button, ToolTip } from '@esgian/esgianui';
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Summary from './Summary';
import FuelConsumptionTable from '@components/Tables/EmissionAndETSTables/FuelConsumptionTable';
import MainEngineFuelTable from '@components/Tables/EmissionAndETSTables/MainEngineFuelTable';
import MainGeneratorFuelTable from '@components/Tables/EmissionAndETSTables/MainGeneratorFuelTable';
import FuelConsumptionOverTimeChart from './FuelConsumptionOverTimeChart';
import EmissionOverTimeChart from './EmissionOverTimeChart';
import ExpandableWrapper from '../../../ExpandableWrapper';
import MyCargoEstimatedETSTable from '../../../Tables/EmissionAndETSTables/EstimatedETS/MyCargoEstimatedETSTable';
import MyCargoExpandedEstimatedETSTable from '../../../Tables/EmissionAndETSTables/EstimatedETS/MyCargoExpandedEstimatedETSTable';
import OverallVoyageEstimatedEtsTable from '../../../Tables/EmissionAndETSTables/EstimatedETS/OverallVoyageEstimatedEtsTable';
import OverallVoyageExpandedEstimatedEtsTable from '../../../Tables/EmissionAndETSTables/EstimatedETS/OverallVoyageExpandedEstimatedEtsTable';
import MyCargoCo2eEmissionTable from '../../../Tables/EmissionAndETSTables/Co2eEmissionTables/MyCargoCo2eEmissionTable';
import OverallVoyageCo2eTable from '../../../Tables/EmissionAndETSTables/Co2eEmissionTables/OverallVoyageCo2eTable';
import MyCargoExpandedCo2eEmissionTable from '../../../Tables/EmissionAndETSTables/Co2eEmissionTables/MyCargoExpandedCo2eEmissionTable';
import OverallVoyageExpandedCo2eEmissionTable from '../../../Tables/EmissionAndETSTables/Co2eEmissionTables/OverallVoyageExpandedCo2eEmissionTable';
import Co2EmissionTable from '../../../Tables/EmissionAndETSTables/Co2EmissionTable';
import EditEstimatedEtsDataModal from '../../../Tables/EmissionAndETSTables/EstimatedETS/EditEstimatedEtsDataModal/EditEstimatedEtsDataModal';
import CalculationTypeDropdown from '../../../Inputs/ETSCalculationTypeDropdown/CalculationTypeDropdown';

function EmissionAndEtsSection({
  esgianData,
  customData,
  isLoadingData,
  fetchEmissionAndEtsData,
  voyagePortCalls,
  calculationType,
  setCalculationType
}) {
  const [openEditModal, setOpenEditModal] = useState(false);

  const [customCalculationData, setCustomCalculationData] = useState({
    etsCostValue: esgianData?.myCargoEuEtsAllowanceSummary?.etsCostPerCo2,
    mainEngineFuelChange: [],
    auxEngineFuelChange: []
  });

  useEffect(() => {
    if (!customCalculationData.mainEngineFuelChange.length && voyagePortCalls.length > 0) {
      setCustomCalculationData({
        etsCostValue: esgianData?.myCargoEuEtsAllowanceSummary?.etsCostPerCo2,
        myCargoWeight: esgianData?.overallVoyageEuEtsAllowanceSummary?.avgCargoWeightTn,
        mainEngineFuelChange: esgianData?.fuelDetails?.mainEngineFuelDurations?.map((item) => {
          return {
            ...item,
            fromPortId: voyagePortCalls[0].portId,
            toPortId: voyagePortCalls[voyagePortCalls.length - 1].portId
          };
        }),
        auxEngineFuelChange: esgianData?.fuelDetails?.mainGeneratorFuelDurations?.map((item) => {
          return {
            ...item,
            fromPortId: voyagePortCalls[0].portId,
            toPortId: voyagePortCalls[voyagePortCalls.length - 1].portId
          };
        })
      });
    }
  }, [voyagePortCalls]);

  const handleEditClick = useCallback(
    (data) => {
      setCustomCalculationData(data);
      setOpenEditModal(false);
      setCalculationType('customised');
      fetchEmissionAndEtsData(data);
    },
    [setCustomCalculationData, fetchEmissionAndEtsData]
  );

  const handleOpenModal = useCallback(() => {
    if (calculationType === 'esgian') {
      setCalculationType('customised');
    }
    setOpenEditModal(true);
  }, [calculationType]);

  const handleCloseModal = useCallback(() => {
    setOpenEditModal(false);
  }, []);

  const handleCalculationTypeChange = useCallback(
    (type) => {
      setCalculationType(type);
      if (type === 'esgian') {
        fetchEmissionAndEtsData(null);
      } else {
        if (customCalculationData) fetchEmissionAndEtsData(customCalculationData);
      }
    },
    [fetchEmissionAndEtsData, customCalculationData]
  );

  const isCustomMode = calculationType === 'customised' && customCalculationData;

  const activeModeData = isCustomMode && customData ? customData : esgianData;
  const alternateDataSource = isCustomMode ? esgianData : null;

  return (
    <Box display="flex" gap={2} flexDirection="column">
      {isLoadingData ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress
            sx={{
              position: 'absolute',
              top: 'calc(50% - 50px)',
              left: 'calc(50% - 50px)'
            }}
            size={100}
          />
        </Box>
      ) : (
        <>
          <Box display="flex" gap={2} alignItems="center">
            <CalculationTypeDropdown
              selectedCalculationType={calculationType}
              onSelect={handleCalculationTypeChange}
              isLoading={isLoadingData}
            />
            <ToolTip title=" Input a customized cargo weight,  modify fuel types and EU Allowance cost to recalculate and generate customized results only visible to the user.">
              <Button onClick={handleOpenModal} disabled={!voyagePortCalls.length}>
                Modify
              </Button>
            </ToolTip>
          </Box>
          <EditEstimatedEtsDataModal
            open={openEditModal}
            onClose={handleCloseModal}
            onSave={handleEditClick}
            voyagePortCalls={voyagePortCalls}
            fuelDetails={esgianData?.fuelDetails}
            customCalculationData={customCalculationData}
          />
          <Summary
            esgianData={esgianData}
            customData={isCustomMode ? customData : null}
            isLoading={isLoadingData}
            activeMode={calculationType}
          />
          <ExpandableWrapper
            title="Estimated ETS"
            contents={[
              {
                main: (
                  <MyCargoEstimatedETSTable
                    isLoading={isLoadingData}
                    myCargoEuEtsAllowanceSummary={activeModeData?.myCargoEuEtsAllowanceSummary}
                    alternativeDataSource={alternateDataSource?.myCargoEuEtsAllowanceSummary}
                  />
                ),
                expanded: [
                  <MyCargoExpandedEstimatedETSTable
                    key={0}
                    isLoading={isLoadingData}
                    myCargoEuEtsAllowanceCost={activeModeData?.myCargoEuEtsAllowanceCost}
                  />
                ]
              },
              {
                main: (
                  <OverallVoyageEstimatedEtsTable
                    isLoading={isLoadingData}
                    overallVoyageEuEtsAllowanceSummary={
                      activeModeData?.overallVoyageEuEtsAllowanceSummary
                    }
                    alternativeDataSource={alternateDataSource?.overallVoyageEuEtsAllowanceSummary}
                  />
                ),
                expanded: [
                  <OverallVoyageExpandedEstimatedEtsTable
                    key={0}
                    isLoading={isLoadingData}
                    overallVoyageEuEtsAllowanceCost={
                      activeModeData?.overallVoyageEuEtsAllowanceCost
                    }
                    alternativeDataSource={alternateDataSource?.overallVoyageEuEtsAllowanceCost}
                  />
                ]
              }
            ]}
          />

          <ExpandableWrapper
            title="CO₂e Emission (GHG Emission)"
            contents={[
              {
                main: (
                  <MyCargoCo2eEmissionTable
                    myCargoToc={activeModeData?.myCargoTocEmission}
                    emissionSummary={activeModeData?.myCargoGhgEmissionSummary}
                    alternativeDataSource={alternateDataSource?.myCargoGhgEmissionSummary}
                    isLoading={isLoadingData}
                  />
                ),
                expanded: [
                  <MyCargoExpandedCo2eEmissionTable
                    key={0}
                    myCargoToc={activeModeData?.myCargoTocEmission}
                    myCargoHoc={activeModeData?.myCargoHocEmission}
                    isLoading={isLoadingData}
                  />
                ]
              },
              {
                main: (
                  <OverallVoyageCo2eTable
                    overallVoyage={activeModeData?.overallVoyageGhgEmission}
                    alternativeDataSource={alternateDataSource?.overallVoyageGhgEmission}
                    isLoading={isLoadingData}
                  />
                ),
                expanded: [
                  <OverallVoyageExpandedCo2eEmissionTable
                    key={0}
                    overallVoyage={activeModeData?.overallVoyageGhgEmission}
                    isLoading={isLoadingData}
                  />
                ]
              }
            ]}
          />
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              CO<sub>2</sub> Emission
            </Typography>
            <Co2EmissionTable
              title={'Overall Voyage'}
              filename={'co2-emission'}
              ciiRating={activeModeData?.ciiRating}
              alternativeDataSource={alternateDataSource?.ciiRating}
              isLoading={isLoadingData}
            />
          </Box>

          <Box>
            <EmissionOverTimeChart
              dailyEmissions={activeModeData?.dailyEmissions}
              isLoading={isLoadingData}
            />
          </Box>

          <Box display="flex" gap={2}>
            <Box flex={1}>
              <FuelConsumptionTable
                fuelDetails={activeModeData?.fuelDetails}
                isLoading={isLoadingData}
              />
            </Box>
            <Box flex={2}>
              <FuelConsumptionOverTimeChart
                fuelDetails={activeModeData?.fuelDetails}
                isLoading={isLoadingData}
              />
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MainEngineFuelTable
                isLoading={isLoadingData}
                fuelTypes={esgianData?.fuelDetails?.fuelTypeIdsFuelNames ?? []}
                mainEngineFuel={activeModeData?.fuelDetails?.mainEngineFuelDurations ?? []}
                voyagePortCalls={voyagePortCalls}
                segmentedPortCalls={customCalculationData.mainEngineFuelChange}
              />
            </Grid>
            <Grid item xs={6}>
              <MainGeneratorFuelTable
                isLoading={isLoadingData}
                fuelTypes={esgianData?.fuelDetails?.fuelTypeIdsFuelNames ?? []}
                mainGeneratorFuel={activeModeData?.fuelDetails?.mainGeneratorFuelDurations ?? []}
                voyagePortCalls={voyagePortCalls}
                segmentedPortCalls={customCalculationData.auxEngineFuelChange}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

EmissionAndEtsSection.propTypes = {
  esgianData: PropTypes.object,
  customData: PropTypes.object,
  isLoadingData: PropTypes.bool.isRequired,
  fetchEmissionAndEtsData: PropTypes.func.isRequired,
  voyagePortCalls: PropTypes.array,
  calculationType: PropTypes.string,
  setCalculationType: PropTypes.func
};

export default EmissionAndEtsSection;
